import { useCallback, useMemo } from 'react';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAsync, AsyncState } from './useAsync';
import { getApiClient } from '../apiClient';

interface AxiosRequestOptions {
  axiosInstance?: AxiosInstance;
  config?: AxiosRequestConfig;
}

/**
 * @deprecated opt instead to use the "useAsync" hook with a callback that calls the API function
 * Fetches data from the given url when the component initially renders, or when parameters change.
 */
export const useApiResponse = <TValue>(
  url: string,
  opts?: AxiosRequestOptions
): AsyncState<() => Promise<TValue>, Error> => {
  const { config, axiosInstance } = opts || {};
  const client = useMemo(() => axiosInstance ?? getApiClient(), [axiosInstance]);

  const executeFn = useCallback(async () => {
    const response = await client.get<TValue>(url, config);
    return response.data;
  }, [url, client, config]);

  const asyncState = useAsync(executeFn);
  return asyncState;
};

/**
 * @deprecated opt instead to use the "useAsync" hook with a callback that calls the API function
 * Makes a post request to a given url and returns the response. Makes the request when the component
 * initially renders or when a parameter changes.
 *
 * @param url Url of the resource to make a request to
 * @param data Data to send in the body of the request
 * @param axiosInstance override Axios client (optional)
 * @returns Response from the API
 */
export const useApiPostResponse = <TReqBody, TValue>(
  url: string,
  opts?: AxiosRequestOptions
): AsyncState<(data: TReqBody) => Promise<TValue>, Error> => {
  const { config, axiosInstance } = opts || {};
  const client = useMemo(() => axiosInstance ?? getApiClient(), [axiosInstance]);

  const executeFn = useCallback(
    async (data: TReqBody) => {
      const response = await client.post<TValue>(url, data, config);
      return response.data;
    },
    [url, client, config]
  );

  const asyncState = useAsync(executeFn);
  return asyncState;
};
